import { ImageSlot, AccidentBatchUpload } from "@/components/ImageUpload";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

function YesNo({ value, onChange, testid }) {
  return (
    <div className="flex gap-2" data-testid={testid}>
      {[true, false].map((v) => (
        <button
          key={String(v)}
          type="button"
          data-testid={`${testid}-${v ? "yes" : "no"}`}
          onClick={() => onChange(v)}
          className={`rounded-sm border px-6 py-2 text-sm font-bold uppercase tracking-wider transition-colors ${
            value === v
              ? v ? "border-primary bg-primary text-primary-foreground" : "border-slate-500 bg-secondary text-foreground"
              : "border-input bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
          }`}
        >
          {v ? "Yes" : "No"}
        </button>
      ))}
    </div>
  );
}

export default function IncidentForm({ value, onChange }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const setTp = (patch) => set({ third_party: { ...(value.third_party || {}), ...patch } });
  const count = value.back_passenger_count || 0;

  return (
    <div className="space-y-8" data-testid="incident-form">
      <div className="space-y-3">
        <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Driver injured?</Label>
        <YesNo testid="driver-injured-toggle" value={value.driver_injured ?? null}
          onChange={(v) => set({ driver_injured: v, driver_injury_details: v ? value.driver_injury_details : "" })} />
        {value.driver_injured && (
          <Textarea
            data-testid="driver-injury-details"
            placeholder="Describe driver injuries..."
            value={value.driver_injury_details || ""}
            onChange={(e) => set({ driver_injury_details: e.target.value })}
            className="rounded-sm border-input bg-card focus-visible:ring-primary"
          />
        )}
      </div>

      <div className="space-y-3">
        <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Front passenger present?</Label>
        <YesNo testid="front-passenger-toggle" value={value.front_passenger_present ?? null}
          onChange={(v) => set({ front_passenger_present: v, front_passenger_injured: v ? value.front_passenger_injured : null })} />
        {value.front_passenger_present && (
          <div className="ml-4 space-y-3 border-l-2 border-primary/40 pl-4">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Front passenger injured?</Label>
            <YesNo testid="front-passenger-injured-toggle" value={value.front_passenger_injured ?? null}
              onChange={(v) => set({ front_passenger_injured: v })} />
          </div>
        )}
      </div>

      <div className="space-y-3">
        <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Back passengers present?</Label>
        <YesNo testid="back-passengers-toggle" value={value.back_passengers_present ?? null}
          onChange={(v) => set({
            back_passengers_present: v,
            back_passenger_count: v ? (value.back_passenger_count || 1) : 0,
            back_passenger_injuries: v ? (value.back_passenger_injuries || [null]) : [],
          })} />
        {value.back_passengers_present && (
          <div className="ml-4 space-y-4 border-l-2 border-primary/40 pl-4">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Quantity (1-6)</Label>
            <div className="flex flex-wrap gap-2" data-testid="back-passenger-count">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`back-passenger-count-${n}`}
                  onClick={() => {
                    const arr = Array.from({ length: n }, (_, i) => value.back_passenger_injuries?.[i] ?? null);
                    set({ back_passenger_count: n, back_passenger_injuries: arr });
                  }}
                  className={`h-10 w-10 rounded-sm border font-mono text-sm font-bold transition-colors ${
                    count === n ? "border-primary bg-primary text-primary-foreground" : "border-input bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            {count > 0 && (
              <div className="space-y-3">
                {Array.from({ length: count }).map((_, i) => (
                  <div key={`passenger-${i}`} className="flex flex-wrap items-center gap-3">
                    <span className="text-sm font-semibold">Passenger {i + 1} injured?</span>
                    <YesNo testid={`back-passenger-${i}-injured`} value={value.back_passenger_injuries?.[i] ?? null}
                      onChange={(v) => {
                        const arr = [...(value.back_passenger_injuries || [])];
                        arr[i] = v;
                        set({ back_passenger_injuries: arr });
                      }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Police Report Statement (batch upload — max 10 photos)</Label>
        <p className="text-[10px] text-muted-foreground">Upload photos of the police report / statement. Stored securely to Google Drive with the other job assets.</p>
        <AccidentBatchUpload values={value.police_report || []} onChange={(v) => set({ police_report: v })} testid="police-report-upload" />
      </div>

      <div className="space-y-3">
        <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Third-party involved?</Label>
        <YesNo testid="third-party-toggle" value={value.third_party_involved ?? null}
          onChange={(v) => set({ third_party_involved: v, third_party: v ? (value.third_party || {}) : {} })} />
        {value.third_party_involved && (
          <div className="ml-4 space-y-4 border-l-2 border-primary/40 pl-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Third-party plate number</Label>
              <Input
                data-testid="third-party-plate-input"
                placeholder="e.g. WXY 1234"
                value={value.third_party?.plate_number || ""}
                onChange={(e) => setTp({ plate_number: e.target.value })}
                className="rounded-sm border-input bg-card font-mono uppercase focus-visible:ring-primary"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <ImageSlot label="Plate Photo" value={value.third_party?.plate_photo || null}
                onChange={(v) => setTp({ plate_photo: v })} testid="tp-plate-photo" />
              <ImageSlot label="Damage Photo" value={value.third_party?.damage_photo || null}
                onChange={(v) => setTp({ damage_photo: v })} testid="tp-damage-photo" />
              <ImageSlot label="Third-party NRIC" value={value.third_party?.nric_photo || null}
                onChange={(v) => setTp({ nric_photo: v, nric_watermarked: !!v })} watermark testid="tp-nric-photo" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
